<template>
  <div class="cf" v-if="rform && rform.formData">
    <div class="cf-cabecera">
      <h2 class="cf-titulo">Condiciones de facturación</h2>
      <span class="cf-sub">
        Valen para todas las líneas. Lo marcado en ámbar no se aplica hasta que se autoriza.
        <template v-if="documento == 'OFERTA'">Se copian al pedido al aceptar la oferta.</template>
      </span>
      <!-- En el pedido se pueden consultar las de la oferta de la que salió, que es de donde se copiaron. -->
      <div class="cf-seg cf-selector" v-if="documento == 'PEDIDO' && rform.formData.idOferta">
        <button type="button" :class="{ elegida: viendoOferta }" @click="verOferta">Oferta</button>
        <button type="button" :class="{ elegida: !viendoOferta }" @click="viendoOferta = false">Pedido</button>
      </div>
    </div>

    <div v-if="viendoOferta" class="cf-banda cf-banda-info">
      <template v-if="ofertaOrigen">
        Condiciones de la oferta {{ ofertaOrigen.codigo }}, de solo lectura: son las que se copiaron a este pedido al
        aceptarla. Las del pedido se cambian en «Pedido».
      </template>
      <template v-else-if="errorOferta">No se ha podido leer la oferta: {{ errorOferta }}</template>
      <template v-else>Leyendo la oferta…</template>
    </div>

    <div class="cf-grid" v-if="doc">
      <div class="cf-principal">
        <div class="cf-tabla">
          <div class="cf-fila" v-for="cond in principales" :key="cond.id">
            <div>
              <div class="cf-nombre">{{ cond.nombre }}</div>
              <div class="cf-defecto">{{ cond.fijo ? 'Dato fijo, no se elige' : 'Por defecto: ' + minuscula(cond.porDefecto) }}</div>
            </div>
            <div class="cf-linea">
              <template v-if="cond.fijo">
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#565a85" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/></svg>
                <span class="cf-fijo">{{ cond.fijo }}</span>
                <span class="cf-nota">{{ cond.fijoDetalle }}</span>
              </template>
              <template v-else>
                <div class="cf-seg">
                  <button
                    v-for="op in cond.opciones"
                    :key="String(op.valor)"
                    type="button"
                    :class="{ elegida: elegida(cond, op), ambar: elegida(cond, op) && enAmbar(cond.clave) }"
                    :disabled="soloLectura"
                    @click="elegir(cond, op)"
                  >{{ op.nombre }}</button>
                </div>

                <input
                  v-if="cond.id == 'numeroPedido' && doc.ordenCompra"
                  class="cf-campo cf-campo-pedido"
                  type="text"
                  maxlength="100"
                  aria-label="Nº de pedido del cliente"
                  placeholder="Nº de pedido"
                  :value="doc.numeroOrdenCompra"
                  :readonly="soloLectura"
                  @input="$set(doc, 'numeroOrdenCompra', $event.target.value)"
                />
                <template v-if="cond.id == 'documentacion' && c.documentacion == 'PACTADA'">
                  <input
                    class="cf-campo cf-campo-documentacion"
                    type="text"
                    maxlength="500"
                    aria-label="Documentación pactada"
                    placeholder="Qué documentación"
                    :value="c.documentacionPactada"
                    :readonly="soloLectura"
                    @input="poner('documentacionPactada', $event.target.value)"
                  />
                  <span class="cf-texto">en</span>
                  <input class="cf-campo cf-campo-dias" type="number" min="1" aria-label="Días" :value="c.documentacionDias"
                    :readonly="soloLectura" @input="poner('documentacionDias', numero($event.target.value))" />
                  <span class="cf-texto">días</span>
                </template>
                <template v-if="cond.id == 'retraso' && c.retraso == 'AMPLIAR'">
                  <span class="cf-texto">a</span>
                  <input class="cf-campo cf-campo-dias" type="number" :min="diasPorDefecto + 1" aria-label="Días" :value="c.retrasoDias"
                    :readonly="soloLectura" @input="poner('retrasoDias', numero($event.target.value))" />
                  <span class="cf-texto">días</span>
                </template>

                <span v-if="cond.clave && avisoDe(cond)" class="cf-aviso" :class="avisoDe(cond).clase">
                  <svg v-if="avisoDe(cond).clase != 'cf-aviso-ok'" aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l10 18H2z"/><path d="M12 10v5M12 18h.01"/></svg>
                  <span>{{ avisoDe(cond).texto }}</span>
                </span>
              </template>
            </div>
          </div>
        </div>

        <div class="cf-otras-titulo">
          Otras condiciones del cliente <span class="cf-sub">· escritas a mano en los comentarios; marca las que apliquen</span>
        </div>
        <div class="cf-otras">
          <label class="cf-casilla" v-for="o in otras" :key="o.campo">
            <input type="checkbox" :checked="c[o.campo] === true" :disabled="soloLectura"
              @change="poner(o.campo, $event.target.checked)" />
            <span class="cf-casilla-texto">
              <span class="cf-casilla-nombre">{{ o.nombre }}</span>
              <span class="cf-casilla-ejemplo">{{ o.ejemplo }}</span>
              <span v-if="avisoDe(o)" class="cf-aviso cf-aviso-casilla" :class="avisoDe(o).clase">{{ avisoDe(o).texto }}</span>
            </span>
            <!-- Ámbar si Ajustes dice que pide autorización: así esta pantalla y la de Ajustes no se contradicen. -->
            <span class="cf-etiqueta" :class="requiere(o.clave) ? 'pide' : 'no-pide'"
              :title="requiere(o.clave) ? 'Necesita autorización' : 'No necesita autorización'">{{ o.retrasa ? 'Retrasa' : 'Requisito' }}</span>
          </label>
        </div>
      </div>

      <div class="cf-lateral">
        <div v-if="exenta" class="cf-tarjeta cf-tarjeta-neutra">
          <div class="cf-tarjeta-titulo">Sin autorización</div>
          {{ estado.motivoExencion }}
        </div>
        <div v-else-if="pendientesLocales.length" class="cf-tarjeta cf-tarjeta-ambar">
          <div class="cf-tarjeta-titulo">
            {{ pendientesLocales.length }} {{ pendientesLocales.length == 1 ? 'condición pendiente' : 'condiciones pendientes' }} de autorizar
          </div>
          <div class="cf-tarjeta-texto">
            Mientras tanto
            <template v-if="documento == 'OFERTA'">la oferta no se puede enviar y</template>
            se factura con los valores por defecto.
          </div>
          <template v-if="!viendoOferta">
            <button type="button" class="cf-boton" :disabled="!!motivoNoPedir" @click="pedirAutorizacion">Pedir autorización</button>
            <div v-if="motivoNoPedir" class="cf-tarjeta-texto">{{ motivoNoPedir }}</div>
          </template>
          <f7-link v-if="puedeAbrirAutorizaciones" :href="rutaAutorizaciones" class="cf-enlace">Abrir OEE › Autorizaciones</f7-link>
        </div>
        <div v-else class="cf-tarjeta cf-tarjeta-ok">Ninguna condición pendiente de autorizar.</div>

        <div class="cf-tarjeta">
          <div class="cf-tarjeta-titulo">Historial</div>
          <div v-if="!historial.length" class="cf-nota">Sin cambios en las condiciones todavía.</div>
          <div v-for="(h, i) in historialVisible" :key="i" class="cf-historial">
            {{ fechaDia(h.fecha) }} · {{ h.texto }}
            <div class="cf-nota" v-if="h.nombre || h.detalle">
              {{ h.detalle }}<template v-if="h.detalle && h.nombre"> · </template>{{ h.nombre }}
            </div>
          </div>
          <a v-if="historial.length > historialResumido" href="#" class="cf-enlace" @click.prevent="historialEntero = !historialEntero">
            {{ historialEntero ? 'Ver solo los últimos' : 'Ver los ' + historial.length + ' cambios' }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Oferta from "../js/db/Oferta.js";
import Pedido from "../js/db/Pedido.js";
import Autorizaciones from "../js/db/Autorizaciones.js";
import CF from "../js/condicionesFacturacion.js";
import { puedeAbrirAutorizaciones, RUTA_AUTORIZACIONES } from "../pages/dashboard/permisos-autorizaciones.js";

/**
 * Bloque «Condiciones de facturación» de la pestaña Pago y Finanzas de oferta y pedido (#905), con
 * la disposición compacta que pidió Dirección el 27/10/2026: una fila por pregunta y las opciones en
 * línea, sin botones de ancho completo.
 *
 * Los valores se editan en `rform.formData` y se guardan con el guardado normal del formulario, para
 * que queden en el histórico con su usuario. Lo que no pasa por el formulario es la petición de
 * autorización: va a la cola y se hace sobre lo **guardado**, por eso el botón exige que no haya
 * cambios sin guardar en el bloque.
 *
 * Qué pide autorización y quién la da lo dice Ajustes › Autorizaciones de condiciones de
 * facturación (`reglas`, de la API): el ámbar de aquí es exactamente lo que allí dice «requiere
 * autorización».
 *
 * Emite `estado` con lo que contesta la API sobre lo guardado, que es lo que usa la oferta para
 * decir junto al botón de envío por qué no se puede enviar.
 */
export default {
  props: {
    rform: {},
    readonly: { type: Boolean, default: false },
    // 'OFERTA' o 'PEDIDO'
    documento: { type: String, required: true }
  },
  data: function () {
    return {
      principales: CF.CONDICIONES_PRINCIPALES,
      otras: CF.OTRAS_CONDICIONES,
      diasPorDefecto: CF.DIAS_RETRASO_POR_DEFECTO,
      // En el pedido, si se están mirando las condiciones de la oferta de la que salió.
      viendoOferta: false,
      ofertaOrigen: null,
      errorOferta: null,
      estado: null,
      // Lo que dice Ajustes; hasta que contesta, la configuración de partida. Si el estado de lo
      // guardado ya las ha traído, mandan esas y no se pisan con una respuesta que llegue después.
      reglas: null,
      reglasDelEstado: false,
      firmaGuardada: null,
      pidiendo: false,
      historialEntero: false,
      historialResumido: 2,
      rutaAutorizaciones: RUTA_AUTORIZACIONES
    };
  },
  computed: {
    doc: function () {
      if (this.viendoOferta) return this.ofertaOrigen;
      return this.rform ? this.rform.formData : null;
    },
    c: function () {
      return (this.doc && this.doc.condicionesFacturacion) || {};
    },
    soloLectura: function () {
      return this.readonly || this.viendoOferta;
    },
    requieren: function () {
      return this.reglas ? this.reglas.filter(r => r.requiereAutorizacion).map(r => r.clave) : CF.REQUIEREN_POR_DEFECTO;
    },
    autorizadores: function () {
      return (this.estado && this.estado.autorizadores) || CF.AUTORIZADORES_POR_DEFECTO;
    },
    exenta: function () {
      return !!(this.estado && this.estado.exenta);
    },
    pendientesLocales: function () {
      return this.exenta ? [] : CF.pendientes(this.c, this.doc && this.doc.ordenCompra, this.requieren);
    },
    historial: function () {
      return (this.c.historial || []).slice().reverse();
    },
    historialVisible: function () {
      return this.historialEntero ? this.historial : this.historial.slice(0, this.historialResumido);
    },
    hayCambiosSinGuardar: function () {
      return this.firmaActual() !== this.firmaGuardada;
    },
    // Por qué no se puede pulsar «Pedir autorización»: qué falta, quién lo desbloquea y dónde (§9).
    motivoNoPedir: function () {
      const que = this.documento == 'PEDIDO' ? 'el pedido' : 'la oferta';
      if (this.pidiendo) return 'Pidiendo…';
      if (!this.doc || !this.doc.id) {
        return 'Guarda primero ' + que + ' con «Guardar», arriba: la autorización se pide sobre lo guardado.';
      }
      if (this.hayCambiosSinGuardar) {
        return 'Tienes cambios sin guardar. Guárdalos con «Guardar», arriba, y pide después la autorización: se pide sobre lo guardado.';
      }
      if (this.estado && this.estado.porSolicitar === 0) {
        return 'Ya está pedida. La resuelve ' + this.autorizadores + ' en OEE › Autorizaciones.';
      }
      return null;
    },
    puedeAbrirAutorizaciones: function () {
      return puedeAbrirAutorizaciones();
    }
  },
  watch: {
    // El formulario sustituye formData entero al cargar y al guardar: es cuando cambia lo guardado.
    // Vue 2 avisa también cuando se AÑADE una propiedad a formData con $set (la primera condición
    // de un documento antiguo, el nº de pedido del cliente, cualquier otro campo del formulario):
    // es el mismo objeto y no se ha guardado nada, así que ni la firma ni el estado se tocan.
    'rform.formData': {
      handler: function (nuevo, anterior) {
        if (nuevo === anterior) return;
        this.sinSegundasIncluidas();
        this.tomarFirma();
        this.leerEstado();
      },
      immediate: true
    }
  },
  created: function () {
    const self = this;
    // En una oferta nueva no hay estado que leer, pero el ámbar tiene que decir ya lo que dice Ajustes.
    Autorizaciones.reglas().then(function (res) {
      if (Array.isArray(res) && !self.reglasDelEstado) self.reglas = res;
    }).catch(function () {
      // Sin respuesta se pinta con la configuración de partida; la API decide igual al pedir y al enviar.
    });
  },
  methods: {
    numero: function (v) {
      return v === '' || v === null ? null : Number(v);
    },
    minuscula: function (t) {
      return t ? t.charAt(0).toLowerCase() + t.slice(1) : t;
    },
    fechaDia: function (fecha) {
      return CF.fechaCorta(fecha).slice(0, 5);
    },
    firmaActual: function () {
      const d = this.rform ? this.rform.formData : null;
      return d ? CF.firma(d.condicionesFacturacion, d.ordenCompra, d.numeroOrdenCompra) : null;
    },
    /**
     * Un documento guardado antes del 27/10/2026 puede traer las segundas «incluidas». La API ya no
     * acepta ese valor y rechazaría el guardado entero, así que se manda «aparte», que es lo único
     * que vale; la API apunta el cambio en el historial al guardar.
     */
    sinSegundasIncluidas: function () {
      const d = this.rform ? this.rform.formData : null;
      if (d && d.condicionesFacturacion && d.condicionesFacturacion.segundas === 'INCLUIDAS') {
        this.$set(d.condicionesFacturacion, 'segundas', 'APARTE');
      }
    },
    tomarFirma: function () {
      this.firmaGuardada = this.firmaActual();
    },
    leerEstado: function () {
      const self = this;
      const d = self.rform ? self.rform.formData : null;
      if (!d || !d.id) {
        self.estado = null;
        self.$emit('estado', null);
        return;
      }
      Autorizaciones.estadoCondiciones(self.documento, d.id).then(function (res) {
        self.estado = res;
        if (res && Array.isArray(res.reglas) && res.reglas.length) {
          self.reglasDelEstado = true;
          self.reglas = res.reglas;
        }
        self.$emit('estado', res);
      }).catch(function () {
        // Sin respuesta no se inventa nada: el ámbar sale de las reglas locales y la API sigue
        // bloqueando el envío por su cuenta.
        self.estado = null;
        self.$emit('estado', null);
      });
    },
    requiere: function (clave) {
      return this.requieren.indexOf(clave) !== -1;
    },
    reglaDe: function (clave) {
      return (this.reglas || []).find(r => r.clave === clave) || null;
    },
    /**
     * Quién lo autorizaría: si ya está pedida, a quien se asignó; si no, a quien iría ahora, que lo
     * dice Ajustes para quien está editando.
     */
    autorizadoresDe: function (clave, decision) {
      if (decision && decision.estado === 'PENDIENTE' && decision.nombresAutorizadores) return decision.nombresAutorizadores;
      const regla = this.reglaDe(clave);
      // La API ha contestado y no queda nadie: no se rellena con un nombre por defecto.
      if (regla) return regla.autorizadores || null;
      return CF.AUTORIZADORES_POR_DEFECTO;
    },
    valorActual: function (cond) {
      if (cond.enCabecera) {
        return !!this.doc[cond.campo];
      }
      const v = this.c[cond.campo];
      if (cond.campo == 'facturacionAutomatica') return v !== false;
      return v === null || v === undefined ? cond.defecto : v;
    },
    elegida: function (cond, op) {
      return this.valorActual(cond) === op.valor;
    },
    elegir: function (cond, op) {
      if (this.soloLectura) return;
      if (cond.enCabecera) {
        this.$set(this.doc, cond.campo, op.valor);
        return;
      }
      this.poner(cond.campo, op.valor);
    },
    poner: function (campo, valor) {
      const d = this.rform.formData;
      if (!d.condicionesFacturacion) {
        this.$set(d, 'condicionesFacturacion', { autorizaciones: [], historial: [] });
      }
      this.$set(d.condicionesFacturacion, campo, valor);
    },
    situacionDe: function (clave) {
      if (this.exenta) return null;
      return CF.situacion(this.c, clave, this.doc && this.doc.ordenCompra, this.requieren);
    },
    enAmbar: function (clave) {
      const s = this.situacionDe(clave);
      return !!s && s.estado !== 'AUTORIZADA';
    },
    /**
     * El aviso de la propia fila, junto al control (§9): si retrasa, quién lo autoriza, con qué se
     * factura mientras tanto y en qué punto está la solicitud. Corto, para que quepa en la línea;
     * si no cabe, es el aviso el que se parte y no las opciones.
     */
    avisoDe: function (cond) {
      if (cond.id == 'retraso' && this.c.retraso == 'AMPLIAR' && !(Number(this.c.retrasoDias) > this.diasPorDefecto)) {
        return { clase: 'cf-aviso-error', texto: 'Pon más de ' + this.diasPorDefecto + ' días: con ' + this.diasPorDefecto + ' o menos no se amplía nada.' };
      }
      const s = this.situacionDe(cond.clave);
      if (!s) {
        if (cond.id == 'retraso' && !this.exenta) {
          return { clase: 'cf-aviso-pista', texto: 'Ampliar exige poner más de ' + this.diasPorDefecto + ' días'
            + (this.requiere('RETRASO_CLIENTE') ? ' y necesita autorización.' : '.') };
        }
        return null;
      }
      const d = s.decision || {};
      if (s.estado == 'AUTORIZADA') {
        return { clase: 'cf-aviso-ok', texto: 'Autorizada por ' + (d.nombreResolutor || d.resueltaPor) + ' el ' + this.fechaDia(d.fechaResolucion) + '.' };
      }
      const mientras = ' Mientras tanto: ' + (cond.porDefecto ? this.minuscula(cond.porDefecto) : 'no se aplica') + '.';
      const quien = this.autorizadoresDe(cond.clave, d);
      let texto = cond.retrasa ? 'Retrasa la factura. ' : '';
      if (s.estado == 'SOLICITADA') {
        texto += 'Pedida el ' + this.fechaDia(d.fechaSolicitud) + ': la resuelve ' + (quien || this.autorizadores) + ' en OEE › Autorizaciones.' + mientras;
        if (d.aclaracion) texto += ' Te piden aclarar: «' + d.aclaracion + '». Contesta por correo.';
      } else if (s.estado == 'RECHAZADA') {
        texto += 'Rechazada por ' + (d.nombreResolutor || d.resueltaPor) + ': «' + (d.motivoResolucion || '') + '».'
          + ' Se factura con el valor por defecto: déjalo así o vuelve a pedirla.';
      } else if (!quien) {
        // §9 · por qué no se puede pedir, quién lo desbloquea y dónde, en la propia fila.
        texto += 'Necesita autorización y ahora no hay nadie que pueda dártela (nadie se autoriza a sí mismo):'
          + ' un administrador asigna quién te autoriza en Ajustes › Autorizaciones de condiciones de facturación.' + mientras;
      } else {
        texto += 'Necesita a ' + quien + '; se pide a la derecha.' + mientras;
      }
      return { clase: 'cf-aviso-ambar', texto: texto };
    },
    verOferta: function () {
      const self = this;
      self.viendoOferta = true;
      if (self.ofertaOrigen || !self.rform.formData.idOferta) return;
      self.errorOferta = null;
      Oferta.get({ itemId: self.rform.formData.idOferta }).then(function (res) {
        self.ofertaOrigen = res;
      }).catch(function (e) {
        self.errorOferta = (e && e.message) || 'error desconocido';
      });
    },
    pedirAutorizacion: function () {
      const self = this;
      const app = self.$f7;
      if (self.motivoNoPedir) return;
      app.dialog.prompt(
        'Por qué lo necesita el cliente. Es lo que lee quien lo autoriza para decidir.',
        'Pedir autorización',
        function (motivo) {
          if (!motivo || !motivo.trim()) {
            app.dialog.alert('Hay que escribir el motivo: es lo que lee quien lo autoriza.');
            return;
          }
          self.pidiendo = true;
          const d = self.rform.formData;
          Autorizaciones.pedirCondiciones(self.documento, d.id, motivo.trim()).then(function (res) {
            self.estado = res;
            self.$emit('estado', res);
            return self.releerAutorizaciones();
          }).then(function () {
            app.toast.create({ text: 'Autorización pedida', position: 'center', closeTimeout: 2000, destroyOnClose: true }).open();
          }).catch(function (e) {
            app.dialog.alert((e && e.message) || 'No se ha podido pedir la autorización.');
          }).then(function () {
            self.pidiendo = false;
          });
        }
      );
    },
    /**
     * Trae del servidor solo las solicitudes y el historial del bloque, sin tocar el resto del
     * formulario: la petición no ha cambiado ningún valor.
     */
    releerAutorizaciones: function () {
      const self = this;
      const adaptador = self.documento == 'PEDIDO' ? Pedido : Oferta;
      const d = self.rform.formData;
      return adaptador.get({ itemId: d.id }).then(function (res) {
        const del = (res && res.condicionesFacturacion) || {};
        if (!d.condicionesFacturacion) {
          self.$set(d, 'condicionesFacturacion', {});
        }
        self.$set(d.condicionesFacturacion, 'autorizaciones', del.autorizaciones || []);
        self.$set(d.condicionesFacturacion, 'historial', del.historial || []);
      });
    }
  }
};
</script>

<style scoped>
/* Medidas y colores de la maqueta de Dirección del 27/10/2026 (filas de 44 px, control segmentado de
   28 px, columna derecha de 300 px). El texto de una opción nunca se parte: el control no se encoge
   (`flex: none`) y, si no cabe, es el aviso de al lado el que pasa a otra línea. */
.cf { margin-top: 4px; color: #1c1c3a; }
.cf-cabecera { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.cf-titulo { margin: 0; font-size: 16px; font-weight: 600; }
.cf-sub { font-size: 12px; font-weight: 400; color: #565a85; }
.cf-cabecera .cf-sub { flex: 1; min-width: 0; }
.cf-grid { display: flex; gap: 24px; align-items: flex-start; }
.cf-principal { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 14px; }
.cf-lateral { width: 300px; flex: none; display: flex; flex-direction: column; gap: 12px; }
@media (max-width: 900px) {
  .cf-grid { flex-direction: column; }
  .cf-lateral { width: 100%; }
}

.cf-tabla { border: 1px solid #dcdcf0; border-radius: 8px; }
.cf-fila { display: grid; grid-template-columns: 250px 1fr; align-items: center; padding: 6px 14px; min-height: 44px; box-sizing: border-box; border-bottom: 1px solid #ececf6; }
.cf-fila:last-child { border-bottom: none; }
.cf-nombre { font-size: 13px; font-weight: 600; }
.cf-defecto { font-size: 11px; color: #565a85; }
.cf-linea { display: flex; align-items: center; gap: 12px; min-width: 0; flex-wrap: wrap; row-gap: 4px; font-size: 13px; }

.cf-seg { display: inline-flex; flex: none; border: 1px solid #c4c6e4; border-radius: 6px; overflow: hidden; }
/* Framework7 pone `button { width: 100% }` a todos los botones: sin un ancho propio, cada opción
   ocupa el control entero y el `overflow: hidden` esconde la segunda. Es lo que escondía «No requiere»
   en Ajustes (Dirección, preview-10, 28/10/2026). */
.cf-seg button { width: auto; height: 28px; line-height: 28px; padding: 0 12px; border: 0; background: #fff; color: #1c1c3a; font-size: 13px; font-family: inherit; white-space: nowrap; flex: none; cursor: pointer; }
.cf-seg button + button { border-left: 1px solid #c4c6e4; }
.cf-seg button.elegida { background: #22224f; color: #fff; font-weight: 500; }
.cf-seg button.elegida.ambar { background: #7a5c00; }
.cf-seg button:disabled { cursor: default; }
.cf-seg button:disabled:not(.elegida) { color: #8a8db0; }
.cf-selector { margin-left: auto; }

.cf-campo { height: 28px; box-sizing: border-box; border: 1px solid #c4c6e4; border-radius: 6px; padding: 0 8px; font-size: 13px; font-family: inherit; flex: none; }
.cf-campo-pedido { width: 150px; }
.cf-campo-documentacion { width: 280px; max-width: 100%; flex: 0 1 280px; }
.cf-campo-dias { width: 52px; }
.cf-texto { font-size: 13px; }
.cf-fijo { font-weight: 500; }
.cf-nota { font-size: 12px; color: #565a85; }

.cf-aviso { font-size: 12px; display: inline-flex; align-items: flex-start; gap: 6px; flex: 1 1 200px; min-width: 0; line-height: 1.35; }
.cf-aviso svg { flex: none; margin-top: 1px; }
.cf-aviso-ambar { color: #6b5000; }
.cf-aviso-ambar svg { color: #7a5c00; }
.cf-aviso-ok { color: #1f5130; }
.cf-aviso-error { color: #a33b1b; }
.cf-aviso-pista { color: #565a85; }
.cf-aviso-casilla { display: block; margin-top: 2px; }

.cf-otras-titulo { font-size: 13px; font-weight: 600; margin-top: 2px; }
.cf-otras { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 16px; }
@media (max-width: 700px) { .cf-otras { grid-template-columns: 1fr; } }
.cf-casilla { display: flex; align-items: center; gap: 10px; border: 1px solid #dcdcf0; border-radius: 8px; padding: 6px 12px; min-height: 40px; box-sizing: border-box; cursor: pointer; }
.cf-casilla input { width: 16px; height: 16px; margin: 0; flex: none; }
.cf-casilla-texto { flex: 1; min-width: 0; }
.cf-casilla-nombre { display: block; font-size: 13px; font-weight: 500; }
.cf-casilla-ejemplo { display: block; font-size: 11px; color: #565a85; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cf-etiqueta { font-size: 11px; border-radius: 10px; padding: 2px 8px; white-space: nowrap; flex: none; }
.cf-etiqueta.pide { background: #fdf1c7; color: #6b5000; }
.cf-etiqueta.no-pide { background: #e9e9fa; color: #3f3f8f; }

.cf-banda { margin-bottom: 12px; padding: 8px 10px; border-radius: 6px; font-size: 12px; border-left: 4px solid; }
.cf-banda-info { background: #E9F0FA; border-left-color: #185FA5; color: #1a2744; }

.cf-tarjeta { border: 1px solid #dcdcf0; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 8px; background: #fff; font-size: 13px; }
.cf-tarjeta-titulo { font-size: 13px; font-weight: 600; }
.cf-tarjeta-texto { font-size: 12px; line-height: 1.4; }
.cf-tarjeta-ambar { background: #fff7dc; border-color: #e8cf7a; color: #4d3b00; }
.cf-tarjeta-ok { background: #f2faf4; border-color: #cfe3d3; color: #1f5130; }
.cf-tarjeta-neutra { border-left: 4px solid #7c8db0; }
.cf-boton { height: 30px; border: 1px solid #7a5c00; background: #fff; color: #4d3b00; border-radius: 6px; font-size: 13px; font-weight: 500; font-family: inherit; cursor: pointer; white-space: nowrap; }
.cf-boton:disabled { opacity: 0.55; cursor: default; }
.cf-enlace { font-size: 12px; color: #3f3f8f; }
.cf-tarjeta-ambar .cf-enlace { color: #4d3b00; text-decoration: underline; }
.cf-historial { font-size: 12px; line-height: 1.4; }
</style>
